import {
  MATERIAL_BATCH_POWDER_QUALITY_STATUS_MAP,
  MATERIAL_BATCH_POWDER_QUALITY_STATUSES,
  MATERIAL_BATCH_WARNING_POWDER_QUALITY_STATUSES,
} from 'src/utils/constants';
import { dedupeBatchMaterials } from 'src/utils/materialUtils';
import { pluralWord } from 'src/utils/stringUtils';
import { getShortUuid } from 'src/utils/url';
import { hasDifferentMaterialTypeSets } from 'src/utils/validation';

export const BLEND_ATTRIBUTE = {
  MATERIAL_TYPE: 'materialType',
  MATERIAL_LOT: 'materialLot',
  PRINTER_TYPE: 'printerType',
  USAGE_CYCLES: 'usageCycles',
  POWDER_QUALITY: 'powderQuality',
};

export const BLEND_ATTRIBUTE_LABELS = {
  [BLEND_ATTRIBUTE.MATERIAL_TYPE]: 'Material Type',
  [BLEND_ATTRIBUTE.MATERIAL_LOT]: 'Material Lot',
  [BLEND_ATTRIBUTE.PRINTER_TYPE]: 'Printer Type History',
  [BLEND_ATTRIBUTE.USAGE_CYCLES]: 'Usage Cycles',
  [BLEND_ATTRIBUTE.POWDER_QUALITY]: 'Powder Quality',
};

// nautilus constants.PowderQualityStatus.get_status_ranking() - higher is worse.
// `done` is deliberately absent there too, so it stays unrankable here.
const POWDER_QUALITY_RANK = {
  [MATERIAL_BATCH_POWDER_QUALITY_STATUSES.NEW]: 1,
  [MATERIAL_BATCH_POWDER_QUALITY_STATUSES.SIEVED]: 2,
  [MATERIAL_BATCH_POWDER_QUALITY_STATUSES.UNSIEVED]: 3,
  [MATERIAL_BATCH_POWDER_QUALITY_STATUSES.UNKNOWN]: 4,
};

export const getPowderQualityLabel = status =>
  MATERIAL_BATCH_POWDER_QUALITY_STATUS_MAP[status] || status || '';

/**
 * Display names for a batch's printer types, deduped by name like the printable Batch
 * QR - two printer-type records can share one. Sorted, because the API aggregates the
 * uris without a guaranteed order. Falls back to the short uuid until the name loads.
 */
export const getPrinterTypeNames = (printerTypeUris = [], printerTypesByUri = {}) =>
  [
    ...new Set(
      printerTypeUris
        // `getShortUuid` returns false rather than a string when a uri carries no uuid,
        // so the uri itself is the last resort - sorting must never meet a non-string.
        .map(uri => printerTypesByUri[uri]?.name || getShortUuid(uri) || uri)
    ),
  ].sort((first, second) => first.localeCompare(second));

/** Compact one-line value for an attribute, used by summaries and step badges. */
export const getAttributeSummary = attribute => {
  switch (attribute?.key) {
    case BLEND_ATTRIBUTE.MATERIAL_TYPE: {
      // `source` is already deduplicated, so this always agrees with the list beside it.
      const names = (attribute.source || []).map(material => material.name);
      return names.length > 1 ? `${names.length} types` : names[0] || '';
    }
    case BLEND_ATTRIBUTE.MATERIAL_LOT: {
      const count = (attribute.source || []).length;
      return `${count} ${pluralWord('lot', count)}`;
    }
    case BLEND_ATTRIBUTE.PRINTER_TYPE: {
      const count = (attribute.source || []).length;
      return `${count} ${pluralWord('printer type', count)}`;
    }
    case BLEND_ATTRIBUTE.USAGE_CYCLES:
      return `${attribute.source ?? 0} ${pluralWord('cycle', attribute.source ?? 0)}`;
    case BLEND_ATTRIBUTE.POWDER_QUALITY:
      return getPowderQualityLabel(attribute.source);
    default:
      return '';
  }
};

// One entry per lot comes back, so the same material repeats on a multi-lot batch.
// Every comparison here is about material types, never about the lots they came from.
const getMaterials = batch => dedupeBatchMaterials(batch?.materials);
const getMaterialLotUris = batch => batch?.material_lots || [];
// Every printer type the batch has been loaded into. Deduped by the API, and empty
// for material that has never been in a printer.
export const getPrinterTypeUris = batch => batch?.printer_type_relationships || [];
const getUsageCycles = batch => batch?.usage_cycles ?? 0;

export const getBlendedMaterialLots = (sourceBatch, destinationBatch) => [
  ...new Set([...getMaterialLotUris(sourceBatch), ...getMaterialLotUris(destinationBatch)]),
];

export const getBlendedPrinterTypes = (sourceBatch, destinationBatch, printerTypesByUri) =>
  getPrinterTypeNames(
    [...getPrinterTypeUris(sourceBatch), ...getPrinterTypeUris(destinationBatch)],
    printerTypesByUri
  );

export const getBlendedMaterials = (sourceBatch, destinationBatch) => {
  const byUri = new Map();
  [...getMaterials(sourceBatch), ...getMaterials(destinationBatch)].forEach(material => {
    if (!byUri.has(material.uri)) {
      byUri.set(material.uri, material);
    }
  });
  return [...byUri.values()];
};

export const getBlendedUsageCycles = (sourceBatch, destinationBatch) =>
  Math.max(getUsageCycles(sourceBatch), getUsageCycles(destinationBatch));

export const getBlendedPowderQuality = (sourceBatch, destinationBatch) => {
  const sourceStatus = sourceBatch?.powder_quality;
  const destinationStatus = destinationBatch?.powder_quality;
  const sourceRank = POWDER_QUALITY_RANK[sourceStatus];
  const destinationRank = POWDER_QUALITY_RANK[destinationStatus];

  if (!sourceRank || !destinationRank) {
    return null;
  }

  return sourceRank >= destinationRank ? sourceStatus : destinationStatus;
};

const hasDifferentSets = (sourceValues, destinationValues) => {
  const source = new Set(sourceValues);
  const destination = new Set(destinationValues);

  if (source.size !== destination.size) {
    return true;
  }

  return [...source].some(value => !destination.has(value));
};

const buildMaterialTypeAttribute = (sourceBatch, destinationBatch) => {
  const blendedMaterials = getBlendedMaterials(sourceBatch, destinationBatch);
  const destinationTypes = new Set(getMaterials(destinationBatch).map(material => material.uri));
  const addsTypes = blendedMaterials.length !== destinationTypes.size;

  return {
    key: BLEND_ATTRIBUTE.MATERIAL_TYPE,
    label: BLEND_ATTRIBUTE_LABELS[BLEND_ATTRIBUTE.MATERIAL_TYPE],
    differs: hasDifferentMaterialTypeSets(sourceBatch, destinationBatch),
    source: getMaterials(sourceBatch),
    destination: getMaterials(destinationBatch),
    becomes: {
      materials: blendedMaterials,
      count: blendedMaterials.length,
      reason: addsTypes ? 'the types from both sides, combined' : 'no new type added',
      changesDestination: addsTypes,
    },
  };
};

const buildMaterialLotAttribute = (sourceBatch, destinationBatch) => {
  const blendedLots = getBlendedMaterialLots(sourceBatch, destinationBatch);
  const addsLots = blendedLots.length !== getMaterialLotUris(destinationBatch).length;

  return {
    key: BLEND_ATTRIBUTE.MATERIAL_LOT,
    label: BLEND_ATTRIBUTE_LABELS[BLEND_ATTRIBUTE.MATERIAL_LOT],
    differs: hasDifferentSets(
      getMaterialLotUris(sourceBatch),
      getMaterialLotUris(destinationBatch)
    ),
    source: getMaterialLotUris(sourceBatch),
    destination: getMaterialLotUris(destinationBatch),
    becomes: {
      lotUris: blendedLots,
      count: blendedLots.length,
      reason: addsLots ? 'the lots from both sides, combined' : 'no new lot added',
      changesDestination: addsLots,
    },
  };
};

// Compared by name, not uri, so the check agrees with what both columns show.
const buildPrinterTypeAttribute = (sourceBatch, destinationBatch, printerTypesByUri) => {
  const source = getPrinterTypeNames(getPrinterTypeUris(sourceBatch), printerTypesByUri);
  const destination = getPrinterTypeNames(getPrinterTypeUris(destinationBatch), printerTypesByUri);
  const blendedPrinterTypes = getBlendedPrinterTypes(
    sourceBatch,
    destinationBatch,
    printerTypesByUri
  );
  // The blend is a superset of the destination, so a change in size is the only way
  // it can gain a printer type - matching how the lots above are counted.
  const addsPrinterTypes = blendedPrinterTypes.length !== destination.length;

  return {
    key: BLEND_ATTRIBUTE.PRINTER_TYPE,
    label: BLEND_ATTRIBUTE_LABELS[BLEND_ATTRIBUTE.PRINTER_TYPE],
    differs: hasDifferentSets(source, destination),
    source,
    destination,
    becomes: {
      names: blendedPrinterTypes,
      count: blendedPrinterTypes.length,
      reason: addsPrinterTypes
        ? 'the printer types from both sides, combined'
        : 'no new printer type added',
      changesDestination: addsPrinterTypes,
    },
  };
};

const buildUsageCyclesAttribute = (sourceBatch, destinationBatch) => {
  const blendedCycles = getBlendedUsageCycles(sourceBatch, destinationBatch);

  return {
    key: BLEND_ATTRIBUTE.USAGE_CYCLES,
    label: BLEND_ATTRIBUTE_LABELS[BLEND_ATTRIBUTE.USAGE_CYCLES],
    differs: getUsageCycles(sourceBatch) !== getUsageCycles(destinationBatch),
    source: getUsageCycles(sourceBatch),
    destination: getUsageCycles(destinationBatch),
    becomes: {
      value: blendedCycles,
      reason: 'the higher of the two, not the total',
      changesDestination: blendedCycles !== getUsageCycles(destinationBatch),
    },
  };
};

const buildPowderQualityAttribute = (sourceBatch, destinationBatch) => {
  const blendedQuality = getBlendedPowderQuality(sourceBatch, destinationBatch);

  return {
    key: BLEND_ATTRIBUTE.POWDER_QUALITY,
    label: BLEND_ATTRIBUTE_LABELS[BLEND_ATTRIBUTE.POWDER_QUALITY],
    differs: sourceBatch?.powder_quality !== destinationBatch?.powder_quality,
    source: sourceBatch?.powder_quality,
    destination: destinationBatch?.powder_quality,
    becomes: blendedQuality && {
      status: blendedQuality,
      reason: 'the lower quality of the two',
      changesDestination: blendedQuality !== destinationBatch?.powder_quality,
      // Unsieved and Unknown are caution states in their own right. A destination
      // that was already Unsieved does not "change", but the operator still ends up
      // with unsieved powder in the printer - that must not read as a green success.
      isCaution: MATERIAL_BATCH_WARNING_POWDER_QUALITY_STATUSES.includes(blendedQuality),
    },
  };
};

const fingerprintValue = value => {
  if (Array.isArray(value)) {
    return value
      .map(item => (typeof item === 'string' ? item : (item?.uri ?? '')))
      .sort()
      .join('+');
  }
  return `${value ?? ''}`;
};

/**
 * Identity of one comparison: the two resources plus the exact values being
 * compared. Any change to either batch yields a different key, so a stored
 * acknowledgement can never carry over to something that is no longer true.
 */
export const buildBlendCheckKey = (mode, sourceBatch, destinationBatch, attributes) => {
  if (!sourceBatch || !attributes?.length) {
    return null;
  }

  const values = attributes
    .map(
      attribute =>
        `${attribute.key}=${fingerprintValue(attribute.source)}>${fingerprintValue(attribute.destination)}`
    )
    .join('|');

  return `${mode}::${sourceBatch.uri}::${destinationBatch?.uri ?? 'none'}::${values}`;
};

/**
 * Compares the two batches an action is about to blend.
 * Returns one entry per attribute, in the order the operator reviews them.
 */
export const compareBlendAttributes = (sourceBatch, destinationBatch, printerTypesByUri) => {
  if (!sourceBatch || !destinationBatch) {
    return [];
  }

  return [
    buildMaterialTypeAttribute(sourceBatch, destinationBatch),
    buildMaterialLotAttribute(sourceBatch, destinationBatch),
    buildPrinterTypeAttribute(sourceBatch, destinationBatch, printerTypesByUri),
    buildUsageCyclesAttribute(sourceBatch, destinationBatch),
    buildPowderQualityAttribute(sourceBatch, destinationBatch),
  ];
};

/**
 * What a single batch contains. Every attribute is returned so the summary can show
 * the whole picture; only types, lots and printer types can ever be mixed, since
 * usage cycles and powder quality are single-valued on a batch.
 */
export const getBlendedBatchAttributes = (batch, printerTypesByUri) => {
  if (!batch) {
    return [];
  }

  const materials = getMaterials(batch);
  const lotUris = getMaterialLotUris(batch);
  const printerTypes = getPrinterTypeNames(getPrinterTypeUris(batch), printerTypesByUri);

  const attribute = (key, differs, source) => ({
    key,
    label: BLEND_ATTRIBUTE_LABELS[key],
    differs,
    source,
    destination: null,
    becomes: null,
  });

  return [
    attribute(BLEND_ATTRIBUTE.MATERIAL_TYPE, materials.length > 1, materials),
    attribute(BLEND_ATTRIBUTE.MATERIAL_LOT, lotUris.length > 1, lotUris),
    attribute(BLEND_ATTRIBUTE.PRINTER_TYPE, printerTypes.length > 1, printerTypes),
    attribute(BLEND_ATTRIBUTE.USAGE_CYCLES, false, getUsageCycles(batch)),
    attribute(BLEND_ATTRIBUTE.POWDER_QUALITY, false, batch.powder_quality),
  ];
};

export const isBlendedBatch = (batch, printerTypesByUri) =>
  getBlendedBatchAttributes(batch, printerTypesByUri).some(attribute => attribute.differs);
